import { z } from "zod";

import { TelegramCommandSchema } from "../../../../schemas/ApiKeyConnection/settings/index";

/**
 * Telegram Bot Profile endpoint schemas
 *
 * GET /api/deployments/:id/telegram/bot-profile
 * - Fetches current profile from Telegram API (always fresh, no caching)
 *
 * PUT /api/deployments/:id/telegram/bot-profile
 * - Updates the Telegram bot's profile using the Bot API
 * - setMyName: Bot display name
 * - setMyDescription: Full description (profile)
 * - setMyShortDescription: Short description (chat list)
 * - setMyCommands: Bot command menu
 */

// =============================================================================
// GET Telegram Bot Profile
// =============================================================================

export const GetTelegramBotProfilePathVarsSchema = z.object({
  id: z.string().uuid()
});

export const GetTelegramBotProfileResponseSchema = z.object({
  id: z.string(),
  username: z.string(),
  displayName: z.string(),
  description: z.string(),
  shortDescription: z.string()
});

export type GetTelegramBotProfilePathVars = z.infer<typeof GetTelegramBotProfilePathVarsSchema>;
export type GetTelegramBotProfileResponse = z.infer<typeof GetTelegramBotProfileResponseSchema>;

// =============================================================================
// UPDATE Telegram Bot Profile
// =============================================================================

// Path variables
export const UpdateTelegramBotProfilePathVarsSchema = z.object({
  id: z.string().uuid()
});

// Bot command (for Telegram API - only command + description)
export const TelegramBotCommandSchema = z.object({
  command: z.string().min(1).max(32),
  description: z.string().min(1).max(256)
});

// Request body
export const UpdateTelegramBotProfileBodySchema = z.object({
  /** Bot display name (1-64 characters) */
  name: z.string().trim().min(1).max(64).optional(),

  /** Bot description shown in profile (0-512 characters) */
  description: z.string().max(512).optional(),

  /** Short description shown in chat list (0-120 characters) */
  shortDescription: z.string().max(120).optional(),

  /** Bot commands for Telegram API (command + description only) */
  commands: z.array(TelegramBotCommandSchema).optional(),

  /** Full command config for persistence (includes response, enabled, isBuiltIn) */
  fullCommands: z.array(TelegramCommandSchema).optional()
});

// Bot info in response
export const TelegramBotInfoSchema = z.object({
  id: z.string(),
  username: z.string(),
  displayName: z.string()
});

// Response data
export const UpdateTelegramBotProfileResponseSchema = z.object({
  success: z.boolean(),
  botInfo: TelegramBotInfoSchema,
  updatedFields: z.array(z.string())
});

// Type exports
export type UpdateTelegramBotProfilePathVars = z.infer<
  typeof UpdateTelegramBotProfilePathVarsSchema
>;
export type UpdateTelegramBotProfileBody = z.infer<typeof UpdateTelegramBotProfileBodySchema>;
export type TelegramBotCommand = z.infer<typeof TelegramBotCommandSchema>;
export type TelegramBotInfo = z.infer<typeof TelegramBotInfoSchema>;
export type UpdateTelegramBotProfileResponse = z.infer<
  typeof UpdateTelegramBotProfileResponseSchema
>;
